import { Alert, Form, Input, Modal, Select, Switch } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createTeamMember, teamErrorCode, teamErrorMessage } from './teamApi'
import type { TeamMember } from './teamTypes'

const MIN_PASSWORD = 8

interface Props {
  open: boolean
  workspaceId: string
  /** Only the Owner may create an Admin directly. */
  canCreateAdmin: boolean
  onClose: () => void
  onCreated: (member: TeamMember) => void
}

/**
 * VistoeraTASK-00525 — create a Managed Account. No invitation email, no
 * personal Workspace: the account lives only in this Workspace. The password is
 * sent once over TLS and never echoed back or kept in component state after
 * submit (the Form is destroyed on close).
 */
export default function CreateMemberModal({ open, workspaceId, canCreateAdmin, onClose, onCreated }: Props) {
  const { t } = useTranslation('team')
  const [form] = Form.useForm<{ display_name: string; email: string; password: string; role: 'admin' | 'editor'; must_change_password: boolean }>()
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const submit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    setServerError(null)
    try {
      const res = await createTeamMember(workspaceId, {
        display_name: values.display_name.trim(),
        email: values.email.trim(),
        password: values.password,
        role: values.role,
        must_change_password: values.must_change_password,
      })
      form.resetFields()
      onCreated(res.data)
    } catch (e) {
      if (teamErrorCode(e) === 'email_already_registered') {
        form.setFields([{ name: 'email', errors: [teamErrorMessage(e, t('create.emailTaken'))] }])
      } else {
        setServerError(teamErrorMessage(e, t('create.failed')))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    setServerError(null)
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('create.title')}
      okText={t('create.submit')}
      cancelText={t('common.cancel')}
      confirmLoading={submitting}
      onOk={() => void submit()}
      onCancel={handleClose}
      destroyOnHidden
      maskClosable={false}
      data-testid="team-create-member-modal"
    >
      <p className="tm-hint" style={{ marginTop: 0 }}>
        {t('create.intro')}
      </p>
      {serverError && <Alert type="error" showIcon title={serverError} style={{ marginBottom: 12 }} />}
      <Form
        form={form}
        layout="vertical"
        requiredMark
        initialValues={{ role: 'editor', must_change_password: true }}
        autoComplete="off"
      >
        <Form.Item
          name="display_name"
          label={t('create.name')}
          rules={[
            { required: true, whitespace: true, message: t('create.nameRequired') },
            { max: 100, message: t('create.nameTooLong') },
          ]}
        >
          <Input maxLength={100} autoComplete="off" data-testid="team-create-name" />
        </Form.Item>
        <Form.Item
          name="email"
          label={t('create.email')}
          rules={[
            { required: true, message: t('create.emailRequired') },
            { type: 'email', message: t('create.emailInvalid') },
          ]}
        >
          <Input autoComplete="off" data-testid="team-create-email" />
        </Form.Item>
        <Form.Item
          name="password"
          label={t('create.password')}
          extra={t('create.passwordHint', { min: MIN_PASSWORD })}
          rules={[
            { required: true, message: t('create.passwordRequired') },
            { min: MIN_PASSWORD, message: t('create.passwordHint', { min: MIN_PASSWORD }) },
          ]}
        >
          <Input.Password autoComplete="new-password" data-testid="team-create-password" />
        </Form.Item>
        <Form.Item name="role" label={t('create.role')}>
          <Select
            data-testid="team-create-role"
            options={[
              { value: 'editor', label: t('role.member') },
              ...(canCreateAdmin ? [{ value: 'admin', label: t('role.admin') }] : []),
            ]}
          />
        </Form.Item>
        <Form.Item name="must_change_password" label={t('create.mustChange')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
